import {
  BoundingBoxInput,
  useWorkscopeDynamicBlockCalculationQuery,
  WorkscopeDynamicBlockCalculationQuery,
  WorkscopeDynamicBlockCalculationQueryVariables,
} from '@/__generated__/graphql';
import { currentCuboid as currentCuboidAtom } from '@/components/Analysis/modules/cuboids';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { CuboidBase } from '@/modules/cuboids/types';
import { cuboidToBoundingBox } from '@/utils/geometry';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as Apollo from '@apollo/client';
import { selectedAssemblyId as selectedAssemblyIdAtom } from '@/components/Analysis/state';

type Options = Pick<
  {
    skip?: boolean;
  } & Apollo.QueryHookOptions<
    WorkscopeDynamicBlockCalculationQuery,
    WorkscopeDynamicBlockCalculationQueryVariables
  >,
  'skip' | 'onCompleted'
>;

export const useFetchWorkscopeDefects = (options?: Options) => {
  const { skip, onCompleted } = options || {};

  const structureId = useGetStructureId();
  const currentCuboid = useAtomValue(currentCuboidAtom);
  const selectedAssemblyId = useAtomValue(selectedAssemblyIdAtom);

  const boundingBox = useMemo(() => {
    if (
      !currentCuboid ||
      !currentCuboid.position ||
      !currentCuboid.rotation ||
      !currentCuboid.scale
    ) {
      return undefined;
    }
    const baseCuboid: CuboidBase = {
      id: currentCuboid?.id,
      position: currentCuboid?.position,
      rotation: currentCuboid?.rotation,
      scale: currentCuboid?.scale,
    };
    return cuboidToBoundingBox(baseCuboid);
  }, [currentCuboid]);

  return useWorkscopeDynamicBlockCalculationQuery({
    skip: skip || !currentCuboid || !boundingBox,
    onCompleted,
    nextFetchPolicy: 'cache-first',
    variables: {
      input: {
        structureId,
        bbox: boundingBox as BoundingBoxInput,
        assemblyId: selectedAssemblyId,
      },
    },
  });
};
